import { FetchArgs } from '@reduxjs/toolkit/query/react'
import { api, infiniteConfigSetting, WorkspacesAPITagType } from '@/api/api'
import getWorkspaceApiUrl from '@/api/utils/get-workspace-api-url'
import {
  AudienceAnalyze,
  FollowerAnalyze,
  SimilarKols,
} from '@/store/kol-detail'
import { Platform } from '@/tests/mock-data/mock-content-distribution'
import {
  FetchCrossCountrySuggestionsRequest,
  FetchHashTagAnalyzeResponse,
  FetchKolAnalyzeResponse,
  FetchKolContentRequest,
  FetchKolContentResponse,
  FetchKolPerformanceRequest,
  FetchKolResourceBasicRequest,
  FetchRelatedKolsRequest,
  FetchSimilarKolsRequest,
  FetchSubscriptionStateResponse,
  KeywordSuggestionsRequest,
  RelatedKol,
  UnlockKolSnapshotRequest,
} from '@/types/api/kols'
import { ListResource } from '@/types/resource-type'
import {
  RecommendedKolsRequest,
  RecommendedKolsResponse,
} from '@/types/schema/api/kol'
import {
  FetchCrossCountrySuggestionsResponse,
  fetchCrossCountrySuggestionsResponseSchema,
  FetchKolPlatformsResponse,
  KeywordSuggestionsResponse,
  keywordSuggestionsResponseSchema,
  KolMetricPerformanceSummary,
  KolMetricPerformanceSummarySchema,
  kolPlatformsSchema,
} from '@/types/schema/kol-schema'
import {
  kolInfoSchema,
  type FetchKolInfoRequest,
  type KolInfo,
} from '@/types/schema/kol/kol-info-schema'
import {
  batchUnlockKolResponseSchema,
  type BatchUnlockKolRequest,
  type BatchUnlockKolResponse,
} from '@/types/schema/kol/unlock'
import getFetchKolContentQuery from '@/utils/api/get-fetch-kol-content-query'
import { camelizeKeys, decamelizeKeys } from '@/utils/humps'
import removeNil from '@/utils/remove-nil'

const kolApi = api.injectEndpoints({
  endpoints: (builder) => ({
    fetchKeywordSuggestions: builder.query<
      KeywordSuggestionsResponse,
      KeywordSuggestionsRequest
    >({
      query: ({ keyword }) => ({
        url: getWorkspaceApiUrl(`/kol/keyword-suggestion`),
        params: { keyword },
      }),
      extraOptions: {
        dataSchema: keywordSuggestionsResponseSchema,
      },
    }),
    fetchCrossCountrySuggestions: builder.query<
      FetchCrossCountrySuggestionsResponse,
      FetchCrossCountrySuggestionsRequest
    >({
      query: ({ workspaceId, with_explore = false, ...params }) => ({
        url: `/workspaces/${workspaceId}/kol/cross-country-suggestions`,
        params: {
          ...params,
          with_explore,
        },
      }),
      extraOptions: {
        dataSchema: fetchCrossCountrySuggestionsResponseSchema,
      },
    }),
    fetchKolLinkAnalysis: builder.query<Platform, FetchKolResourceBasicRequest>(
      {
        query: ({ workspaceId, radar_uuid, dc_id_hashed }) => {
          return {
            url: `/v3/workspaces/${workspaceId}/kol/linkAnalytics`,
            params: removeNil({ radar_uuid, dc_id_hashed }),
          }
        },
        transformResponse: (response: object) => {
          return camelizeKeys(response) as unknown as Platform
        },
        providesTags: [WorkspacesAPITagType.KolInfo],
      },
    ),
    fetchKolAnalyze: builder.query<
      FetchKolAnalyzeResponse,
      FetchKolResourceBasicRequest
    >({
      query: ({ workspaceId, radar_uuid, dc_id_hashed }) => {
        return {
          url: `/v3/workspaces/${workspaceId}/kol/analyze`,
          params: removeNil({ radar_uuid, dc_id_hashed }),
        }
      },
      transformResponse: (response: object) => {
        return camelizeKeys(response) as unknown as FetchKolAnalyzeResponse
      },
      providesTags: [WorkspacesAPITagType.KolInfo],
    }),
    fetchKolFollowerAnalysis: builder.query<
      FollowerAnalyze,
      FetchKolResourceBasicRequest
    >({
      query: ({ workspaceId, radar_uuid, dc_id_hashed }) => {
        return {
          url: `/v3/workspaces/${workspaceId}/kol/follower/analyze`,
          params: removeNil({ radar_uuid, dc_id_hashed }),
        }
      },
      transformResponse: (response: { data: object }) => {
        return camelizeKeys(response.data) as unknown as FollowerAnalyze
      },
      providesTags: [WorkspacesAPITagType.KolInfo],
    }),
    fetchKolAudienceAnalysis: builder.query<
      AudienceAnalyze,
      FetchKolResourceBasicRequest
    >({
      query: ({ workspaceId, radar_uuid, dc_id_hashed }) => {
        return {
          url: `/v3/workspaces/${workspaceId}/kol/audience/analyze`,
          params: removeNil({ radar_uuid, dc_id_hashed }),
        }
      },
      transformResponse: (response: { data: object }) => {
        return camelizeKeys(response.data) as unknown as AudienceAnalyze
      },
      providesTags: [WorkspacesAPITagType.KolInfo],
    }),
    fetchKolInfo: builder.query<KolInfo, FetchKolInfoRequest>({
      query: ({ radar_uuid, dc_id_hashed }) => {
        return {
          url: getWorkspaceApiUrl('/kol/info', 'v3'),
          params: removeNil({ radar_uuid, dc_id_hashed }),
        }
      },
      extraOptions: {
        dataSchema: kolInfoSchema,
      },
      providesTags: [WorkspacesAPITagType.KolInfo],
    }),
    fetchKolContent: builder.query<
      FetchKolContentResponse,
      FetchKolContentRequest
    >({
      query: getFetchKolContentQuery,
      transformResponse: (response: object) => {
        return camelizeKeys(response) as unknown as FetchKolContentResponse
      },
      providesTags: [WorkspacesAPITagType.KolContent],
    }),
    fetchKolContentInfinite: builder.query<
      FetchKolContentResponse,
      FetchKolContentRequest
    >({
      query: getFetchKolContentQuery,
      ...infiniteConfigSetting<
        FetchKolContentRequest,
        FetchKolContentResponse
      >(),
      transformResponse: (response: object) => {
        return camelizeKeys(response) as unknown as FetchKolContentResponse
      },
      providesTags: [WorkspacesAPITagType.KolInfo],
    }),
    fetchKolSubscriptionState: builder.query<
      FetchSubscriptionStateResponse,
      FetchKolResourceBasicRequest
    >({
      query: ({ workspaceId, radar_uuid, dc_id_hashed }) => {
        return {
          url: `/v3/workspaces/${workspaceId}/kol/subscription`,
          params: removeNil({ radar_uuid, dc_id_hashed }),
        }
      },
      transformResponse: (response: object) => {
        return camelizeKeys(
          response,
        ) as unknown as FetchSubscriptionStateResponse
      },
      providesTags: [
        WorkspacesAPITagType.KolInfo,
        WorkspacesAPITagType.Subscription,
      ],
    }),
    fetchSimilarKols: builder.query<
      ListResource<SimilarKols>,
      FetchSimilarKolsRequest
    >({
      query: ({
        workspaceId,
        radar_uuid,
        dc_id_hashed,
        platform,
        per_page = 10,
        page = 1,
      }) => {
        return {
          url: `/v3/workspaces/${workspaceId}/kol/similar-kols`,
          params: removeNil({
            platform,
            per_page,
            page,
            radar_uuid,
            dc_id_hashed,
          }),
        }
      },
      transformResponse: (response: object) => {
        return camelizeKeys(response) as unknown as ListResource<SimilarKols>
      },
      providesTags: [WorkspacesAPITagType.KolInfo],
    }),
    // 拿複數 uuid 去算相似網紅
    fetchRecommendedKols: builder.query<
      RecommendedKolsResponse,
      RecommendedKolsRequest
    >({
      query: ({ beforeQueryStarts, onQueryFulfilled, ...body }) => {
        return {
          url: getWorkspaceApiUrl(`/kol/recommended-kols`, 'v3'),
          method: 'POST',
          body,
          beforeQueryStarts,
          onQueryFulfilled,
        }
      },
      providesTags: [WorkspacesAPITagType.DependPredictiveReportCandidate],
    }),
    fetchRelatedBrands: builder.query<
      ListResource<RelatedKol>,
      FetchRelatedKolsRequest
    >({
      query: ({
        workspaceId,
        radar_uuid,
        dc_id_hashed,
        ...payload
      }): FetchArgs => {
        return {
          url: `/v3/workspaces/${workspaceId}/kol/related-kols`,
          params: removeNil({
            ...decamelizeKeys(payload),
            radar_uuid,
            dc_id_hashed,
          }),
        }
      },
      transformResponse: (response: object): ListResource<RelatedKol> => {
        return camelizeKeys(response) as unknown as ListResource<RelatedKol>
      },
      providesTags: [WorkspacesAPITagType.KolInfo],
    }),
    fetchHashTagAnalysis: builder.query<
      FetchHashTagAnalyzeResponse,
      FetchKolResourceBasicRequest
    >({
      query: ({ workspaceId, radar_uuid, dc_id_hashed }) => {
        return {
          url: `/v3/workspaces/${workspaceId}/kol/statistic-hashtags`,
          params: removeNil({ radar_uuid, dc_id_hashed }),
        }
      },
      transformResponse: (response: object) => {
        return camelizeKeys(response) as unknown as FetchHashTagAnalyzeResponse
      },
      providesTags: [WorkspacesAPITagType.KolInfo],
    }),
    fetchKolPerformanceSummary: builder.query<
      KolMetricPerformanceSummary,
      FetchKolPerformanceRequest
    >({
      query: ({ radar_uuid, platform_type, scope }) => {
        const url = getWorkspaceApiUrl(`/kol/performance-summary`, 'v3')

        return {
          url,
          params: { platform_type, scope, radar_uuid },
        }
      },
      extraOptions: {
        dataSchema: KolMetricPerformanceSummarySchema,
      },
      providesTags: [WorkspacesAPITagType.KolMetricPerformanceSummary],
    }),
    unlockKolSnapshot: builder.mutation<void, UnlockKolSnapshotRequest>({
      query: ({ kolUuid, from, is_clicked_from_drawer }) => {
        return {
          url: getWorkspaceApiUrl(`/kols/${kolUuid}/unlock`, 'v3'),
          method: 'POST',
          body: { from, is_clicked_from_drawer },
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.KolInfo,
        WorkspacesAPITagType.Collection,
        WorkspacesAPITagType.QuotaWalletUsage,
        WorkspacesAPITagType.SearchSimilarKols,
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
        WorkspacesAPITagType.KolMetricPerformanceSummary,
      ],
    }),
    batchUnlockKols: builder.mutation<
      BatchUnlockKolResponse,
      BatchUnlockKolRequest
    >({
      query: (body) => {
        return {
          url: getWorkspaceApiUrl(`/kols/unlock`, 'v3'),
          method: 'POST',
          body,
        }
      },
      extraOptions: {
        dataSchema: batchUnlockKolResponseSchema,
      },
      invalidatesTags: [
        WorkspacesAPITagType.KolInfo,
        WorkspacesAPITagType.Collection,
        WorkspacesAPITagType.QuotaWalletUsage,
        WorkspacesAPITagType.SearchSimilarKols,
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
      ],
    }),
    fetchKolPlatforms: builder.query<
      FetchKolPlatformsResponse,
      Omit<FetchKolResourceBasicRequest, 'workspaceId' | 'dc_id_hashed'>
    >({
      query: ({ radar_uuid }) => {
        return {
          url: getWorkspaceApiUrl('/kol/platforms', 'v3'),
          params: { radar_uuid },
        }
      },
      extraOptions: {
        dataSchema: kolPlatformsSchema,
      },
      providesTags: [WorkspacesAPITagType.KolPlatforms],
    }),
  }),
  overrideExisting: false,
})

export const {
  useFetchKolLinkAnalysisQuery,
  useLazyFetchKolLinkAnalysisQuery,
  useFetchCrossCountrySuggestionsQuery,
  useFetchKolAnalyzeQuery,
  useLazyFetchKolAnalyzeQuery,
  useFetchKolFollowerAnalysisQuery,
  useFetchKolAudienceAnalysisQuery,
  useLazyFetchKolContentQuery,
  useFetchKolContentInfiniteQuery,
  useFetchKolSubscriptionStateQuery,
  useFetchSimilarKolsQuery,
  useFetchRelatedBrandsQuery,
  useLazyFetchRelatedBrandsQuery,
  useFetchHashTagAnalysisQuery,
  useUnlockKolSnapshotMutation,
  useFetchKolPlatformsQuery,
} = kolApi
export const useFetchKolInfoQuery = kolApi.endpoints.fetchKolInfo.useQuery
export const useBatchUnlockKolsMutation =
  kolApi.endpoints.batchUnlockKols.useMutation
export const useFetchKeywordSuggestionsQuery =
  kolApi.endpoints.fetchKeywordSuggestions.useQuery
export const useFetchRecommendedKolsQuery =
  kolApi.endpoints.fetchRecommendedKols.useQuery
export const useFetchKolPerformanceSummaryQuery =
  kolApi.endpoints.fetchKolPerformanceSummary.useQuery
